import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  BarChart3, 
  Target, 
  TrendingUp, 
  Award, 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp,
  FileText,
  Copy,
  Check,
  Loader2
} from 'lucide-react';
import { theme } from '../styles/theme';
import { ScoreReport } from '../types';

const ReportContainer = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  margin-top: ${theme.spacing[4]};
`;

const ReportHeader = styled.div`
  background: linear-gradient(135deg, ${theme.colors.accent[500]}, ${theme.colors.accent[600]});
  color: white;
  padding: ${theme.spacing[6]};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1));
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing[4]};
    text-align: center;
  }
`;

const ReportTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};

  h2 {
    margin: 0;
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.bold};
    color: white;
  }

  p {
    margin: ${theme.spacing[1]} 0 0 0;
    opacity: 0.9;
    font-size: ${theme.typography.fontSize.sm};
    color: white;
  }
`;

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${theme.borderRadius.md};
  backdrop-filter: blur(10px);
`;

const OverallScore = styled.div`
  text-align: center;
`;

const ScoreCircle = styled.div<{ score: number }>`
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: conic-gradient(
    from 0deg,
    rgba(255, 255, 255, 0.2) 0deg,
    rgba(255, 255, 255, 0.9) ${({ score }) => (score / 100) * 360}deg,
    rgba(255, 255, 255, 0.2) ${({ score }) => (score / 100) * 360}deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing[2]} auto;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 64px;
    height: 64px;
    background: ${theme.colors.accent[500]};
    border-radius: ${theme.borderRadius.full};
  }
`;

const ScoreText = styled.div`
  position: relative;
  z-index: 1;
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: white;
`;

const ScoreLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  opacity: 0.9;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${theme.borderRadius.md};
  color: white;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
`;

const ReportBody = styled.div`
  padding: ${theme.spacing[6]};
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing[6]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};

  h3 {
    margin: 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.lg};
  }
`;

const SectionIcon = styled.div`
  color: ${theme.colors.accent[500]};
`;

const ScoresGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const ScoreCard = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  transition: all ${theme.transitions.base};

  &:hover {
    border-color: ${theme.colors.accent[400]};
    box-shadow: ${theme.shadows.sm};
    transform: translateY(-1px);
  }
`;

const ScoreCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[3]};
`;

const ScoreCardTitle = styled.h4`
  margin: 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.base};
`;

const ScoreValue = styled.div<{ score: number; maxScore: number }>`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ score, maxScore }) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return theme.colors.success[600];
    if (percentage >= 60) return theme.colors.warning[600];
    return theme.colors.error[600];
  }};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.border};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin: ${theme.spacing[2]} 0;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.accent[400]}, ${theme.colors.accent[500]});
  border-radius: ${theme.borderRadius.full};
  width: ${({ percentage }) => Math.min(percentage, 100)}%;
  transition: width ${theme.transitions.base};
`;

const ScoreCardBody = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const CriteriaList = styled.ul`
  margin: ${theme.spacing[2]} 0 0 0;
  padding: 0 0 0 ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.xs};
  
  li {
    margin-bottom: ${theme.spacing[1]};
    color: ${theme.colors.textMuted};
  }
`;

const AnalysisSection = styled.div<{ expanded: boolean }>`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
`;

const AnalysisHeader = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border-bottom: 1px solid ${theme.colors.border};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.surfaceHover};
  }
`;

const AnalysisContent = styled.div<{ expanded: boolean }>`
  padding: ${({ expanded }) => expanded ? theme.spacing[4] : '0'};
  max-height: ${({ expanded }) => expanded ? 'none' : '0'};
  overflow: hidden;
  transition: all ${theme.transitions.base};
  
  p {
    margin: 0 0 ${theme.spacing[3]} 0;
    color: ${theme.colors.textSecondary};
    line-height: ${theme.typography.lineHeight.relaxed};
    font-size: ${theme.typography.fontSize.sm};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ImprovementList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: ${theme.spacing[2]};
    margin-bottom: ${theme.spacing[2]};
    padding: ${theme.spacing[2]};
    background: ${theme.colors.warning[100]};
    border-left: 3px solid ${theme.colors.warning[500]};
    border-radius: 0 ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0;
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[8]};
  text-align: center;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundSecondary};
  border: 2px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${theme.colors.accent[100]};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[3]};
  color: ${theme.colors.accent[500]};
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[700]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-bottom: ${theme.spacing[3]};
  
  .spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.warning[100]};
  border: 1px solid ${theme.colors.warning[500]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.warning[700]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-bottom: ${theme.spacing[3]};
`;

const FormattedContent = styled.div`
  line-height: 1.6;
  
  h3 {
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    margin: ${theme.spacing[4]} 0 ${theme.spacing[2]} 0;
    font-size: ${theme.typography.fontSize.lg};
  }
  
  strong {
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
  }
  
  p {
    margin: ${theme.spacing[2]} 0;
    color: ${theme.colors.textSecondary};
  }
  
  ul, ol {
    margin: ${theme.spacing[2]} 0;
    padding-left: ${theme.spacing[6]};
    
    li {
      margin: ${theme.spacing[1]} 0;
      color: ${theme.colors.textSecondary};
    }
  }
`;

interface ScoreReportDisplayProps {
  scoreReport?: ScoreReport;
}

const ScoreReportDisplay: React.FC<ScoreReportDisplayProps> = ({
  scoreReport,
}) => {
  const [expandedAnalysis, setExpandedAnalysis] = useState(false);
  const [copied, setCopied] = useState(false);

  // Enhanced markdown-to-HTML converter for AI generated content
  const formatContent = (content: string) => {
    if (!content) return content;
    
    return content
      // Convert headings
      .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Convert bold text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert italic text (single asterisks)
      .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
      // Convert inline code
      .replace(/`([^`]+?)`/g, '<code>$1</code>')
      // Convert bullet points
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // Convert numbered lists
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // Convert line breaks to paragraphs
      .split('\n\n')
      .map(paragraph => {
        const trimmed = paragraph.trim();
        if (!trimmed) return '';
        
        // Handle headings
        if (trimmed.startsWith('<h1>') || trimmed.startsWith('<h2>') || 
            trimmed.startsWith('<h3>') || trimmed.startsWith('<h4>')) {
          return trimmed;
        }
        
        // Handle lists
        if (trimmed.includes('<li>')) {
          const listItems = trimmed.split('\n').filter(line => line.trim());
          if (listItems.every(item => item.includes('<li>'))) {
            return `<ul>${listItems.join('')}</ul>`;
          }
        }
        
        // Handle regular paragraphs
        return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
      })
      .join('');
  };

  if (!scoreReport) {
    return (
      <EmptyState>
        <EmptyIcon>
          <BarChart3 size={24} />
        </EmptyIcon>
        <p>Detailed score report will appear here once analysis is complete...</p>
      </EmptyState>
    );
  }

  const handleCopy = async () => {
    const reportText = `
Score Report Summary

Overall Score: ${scoreReport.overall_score}/100

Section Scores:
${scoreReport.section_scores.map(section => `- ${section.criterion || section.section_name || 'Criterion'}: ${section.score}/${section.max_score ?? 10}${typeof section.weight === 'number' ? ` (weight ${section.weight}%)` : ''}`).join('\n')}

Analysis:
${scoreReport.detailed_analysis}

Key Improvement Areas:
${scoreReport.improvement_areas.map(area => `- ${area}`).join('\n')}

Competitive Positioning:
${scoreReport.competitive_positioning}
    `.trim();

    await navigator.clipboard.writeText(reportText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <ReportContainer>
      <ReportHeader>
        <HeaderContent>
          <ReportTitle>
            <TitleIcon>
              <BarChart3 size={24} />
            </TitleIcon>
            <div>
              <h2>Detailed Score Report</h2>
              <p>Comprehensive analysis and scoring breakdown</p>
            </div>
          </ReportTitle>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: `${theme.spacing[3]}` }}>
            
            
            <OverallScore>
              <ScoreCircle score={scoreReport.overall_score}>
                <ScoreText>{scoreReport.overall_score}</ScoreText>
              </ScoreCircle>
              <ScoreLabel>Overall Score</ScoreLabel>
            </OverallScore>
          </div>
        </HeaderContent>
      </ReportHeader>

      <ReportBody>
        {/* Section Scores */}
        <Section>
          <SectionHeader>
            <SectionIcon>
              <Target size={20} />
            </SectionIcon>
            <h3>Section Breakdown</h3>
          </SectionHeader>
          
          <ScoresGrid>
            {scoreReport.section_scores.map((section, index) => {
              // Backend schema uses {criterion, weight, score, justification}
              // (reviewer's criteria_scores passed through verbatim). Legacy
              // fallbacks cover pre-migration payloads that used
              // {section_name, max_score, comments, criteria[]}.
              const title = section.criterion || section.section_name || 'Criterion';
              const maxScore = section.max_score ?? 10;
              const score = typeof section.score === 'number' ? section.score : 0;
              const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
              const justification = section.justification || section.comments || '';
              const subCriteria = section.criteria || [];
              return (
                <ScoreCard key={index}>
                  <ScoreCardHeader>
                    <ScoreCardTitle>
                      {title}
                      {typeof section.weight === 'number' ? ` (${section.weight}%)` : ''}
                    </ScoreCardTitle>
                    <ScoreValue score={score} maxScore={maxScore}>
                      {score}/{maxScore}
                    </ScoreValue>
                  </ScoreCardHeader>

                  <ProgressBar>
                    <ProgressFill percentage={pct} />
                  </ProgressBar>

                  <ScoreCardBody>
                    {justification}
                    {subCriteria.length > 0 && (
                      <CriteriaList>
                        {subCriteria.map((criterion, idx) => (
                          <li key={idx}>{criterion}</li>
                        ))}
                      </CriteriaList>
                    )}
                  </ScoreCardBody>
                </ScoreCard>
              );
            })}
          </ScoresGrid>
        </Section>

        {/* Executive Summary — grounded prose from create_initial_score_report */}
        <Section>
          <SectionHeader>
            <SectionIcon>
              <FileText size={20} />
            </SectionIcon>
            <h3>Executive Summary</h3>
          </SectionHeader>

          <AnalysisSection expanded={true}>
            <AnalysisContent expanded={true}>
              <FormattedContent
                dangerouslySetInnerHTML={{
                  __html: formatContent(scoreReport.executive_summary || scoreReport.summary || '')
                }}
              />
            </AnalysisContent>
          </AnalysisSection>
        </Section>

        {/* Detailed Analysis — cites specific criteria and findings */}
        <Section>
          <SectionHeader>
            <SectionIcon>
              <TrendingUp size={20} />
            </SectionIcon>
            <h3>Detailed Analysis</h3>
          </SectionHeader>

          <AnalysisSection expanded={expandedAnalysis}>
            <AnalysisHeader onClick={() => setExpandedAnalysis(!expandedAnalysis)}>
              <span>View detailed analysis and insights</span>
              {expandedAnalysis ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </AnalysisHeader>
            <AnalysisContent expanded={expandedAnalysis}>
              <FormattedContent
                dangerouslySetInnerHTML={{
                  __html: formatContent(scoreReport.detailed_analysis || '')
                }}
              />
            </AnalysisContent>
          </AnalysisSection>
        </Section>

        {/* Key Improvement Areas — sourced from the reviewer's weaknesses list */}
        {scoreReport.improvement_areas && scoreReport.improvement_areas.length > 0 && (
          <Section>
            <SectionHeader>
              <SectionIcon>
                <AlertTriangle size={20} />
              </SectionIcon>
              <h3>Key Improvement Areas</h3>
            </SectionHeader>

            <ImprovementList>
              {scoreReport.improvement_areas.map((area, index) => (
                <li key={index}>
                  <AlertTriangle size={16} style={{ marginTop: '2px', flexShrink: 0, color: theme.colors.warning[500] }} />
                  {area}
                </li>
              ))}
            </ImprovementList>
          </Section>
        )}

        {/* Competitive Positioning — grounded in the scored strengths/weaknesses */}
        <Section>
          <SectionHeader>
            <SectionIcon>
              <Award size={20} />
            </SectionIcon>
            <h3>Competitive Positioning</h3>
          </SectionHeader>

          <AnalysisSection expanded={true}>
            <AnalysisContent expanded={true}>
              <FormattedContent
                dangerouslySetInnerHTML={{
                  __html: formatContent(scoreReport.competitive_positioning || '')
                }}
              />
            </AnalysisContent>
          </AnalysisSection>
        </Section>
      </ReportBody>
    </ReportContainer>
  );
};

export default ScoreReportDisplay;